import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, X, MessagesSquare, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { inviteApi, AUDIENCE_TYPES, PRIORITIES } from '../../lib/inviteApi';
import InviteStatusBadge from '../../components/invite/InviteStatusBadge';
import ContactProfile from '../../components/invite/ContactProfile';

export default function ProspectsTab({ exhibitionId, onNavigate }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '', priority: '', relationship_status: '', search: '', hideTest: false });
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.category) params.category = filter.category;
      if (filter.priority) params.priority = filter.priority;
      if (filter.relationship_status) params.relationship_status = filter.relationship_status;
      if (filter.search) params.search = filter.search;
      const d = await inviteApi.listContacts(exhibitionId, params);
      setContacts(d);
    } catch (err) {
      toast.error(errorDetail(err, 'Failed to load contacts'));
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [exhibitionId, filter.category, filter.priority, filter.relationship_status]);

  const filtered = filter.hideTest
    ? contacts.filter((c) => !isTestRecord(c))
    : contacts;

  return (
    <div className="space-y-4" data-testid="prospects-tab">
      <div className="flex flex-wrap items-center gap-2">
        <select value={filter.category} onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))} className="invite-select" data-testid="contacts-filter-category">
          <option value="">All categories</option>
          {AUDIENCE_TYPES.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={filter.priority} onChange={(e) => setFilter((f) => ({ ...f, priority: e.target.value }))} className="invite-select">
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input
          value={filter.search}
          onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && refresh()}
          placeholder="Search name / email / org"
          className="invite-input min-w-[220px]"
          data-testid="contacts-search"
        />
        <button onClick={refresh} data-clickable="true" className="text-[10px] font-mono px-3 py-1.5 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">Apply</button>
        <label className="inline-flex items-center gap-2 text-[10px] font-mono text-[#888] cursor-pointer ml-1">
          <input type="checkbox" checked={filter.hideTest} onChange={(e) => setFilter((f) => ({ ...f, hideTest: e.target.checked }))} data-testid="contacts-hide-test" />
          Hide test records
        </label>
        <div className="ml-auto">
          <button onClick={() => setShowForm(true)} data-clickable="true" data-testid="contact-add-btn" className="inline-flex items-center gap-1.5 text-[10px] font-mono px-3 py-1.5 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em]">
            <Plus className="w-3 h-3" /> Add Contact
          </button>
        </div>
      </div>

      <div className="border border-[#1a1a1a] overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="text-[#666] uppercase tracking-[0.14em]">
            <tr className="border-b border-[#161616]">
              <th className="text-left p-2.5">Name</th>
              <th className="text-left p-2.5">Org</th>
              <th className="text-left p-2.5">Email</th>
              <th className="text-left p-2.5">Category</th>
              <th className="text-left p-2.5">Priority</th>
              <th className="text-left p-2.5">Relationship</th>
              <th className="text-left p-2.5">Thread</th>
              <th className="text-right p-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-4 text-[#555]">Loading…</td></tr>}
            {!loading && !filtered.length && <tr><td colSpan={8} className="p-4 text-[#555]">No contacts yet.</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`prospect-row-${c.id}`} className="border-b border-[#0e0e0e] hover:bg-[#0c0c0c] cursor-pointer" onClick={() => setSelectedId(c.id)}>
                <td className="p-2.5 text-white">{c.name || '—'}</td>
                <td className="p-2.5 text-[#aaa]">{c.organization_name || '—'}</td>
                <td className="p-2.5 text-[#888]">{c.email || '—'}</td>
                <td className="p-2.5 text-[#aaa]">{c.category || '—'}</td>
                <td className="p-2.5"><InviteStatusBadge status={c.exhibition_link?.priority} /></td>
                <td className="p-2.5"><InviteStatusBadge status={c.exhibition_link?.relationship_status} /></td>
                <td className="p-2.5">
                  {c.latest_thread ? <InviteStatusBadge status={c.latest_thread.status} /> : <span className="text-[#444]">—</span>}
                </td>
                <td className="p-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1.5">
                    {c.latest_thread && (
                      <button onClick={() => onNavigate?.('conversations', { threadId: c.latest_thread.id })} data-clickable="true" data-testid={`contact-thread-${c.id}`} className="p-1.5 border border-[#222] text-[#888] hover:text-[#90E0EF] hover:border-[#444]" aria-label="Open thread">
                        <MessagesSquare className="w-3 h-3" />
                      </button>
                    )}
                    <button onClick={() => setSelectedId(c.id)} data-clickable="true" data-testid={`contact-view-${c.id}`} className="p-1.5 border border-[#222] text-[#888] hover:text-[#90E0EF] hover:border-[#444]" aria-label="View profile">
                      <Eye className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {showForm && (
          <FormModal exhibitionId={exhibitionId} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); refresh(); }} />
        )}
        {selectedId && (
          <ContactProfile
            contactId={selectedId}
            exhibitionId={exhibitionId}
            onClose={() => setSelectedId(null)}
            onChanged={refresh}
            onOpenThread={(id) => { setSelectedId(null); onNavigate?.('conversations', { threadId: id }); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function isTestRecord(c) {
  const name = (c.name || '').toLowerCase();
  const email = (c.email || '').toLowerCase();
  return name.startsWith('test ') || name.startsWith('test_') || name === 'test' || email.startsWith('test_') || email.startsWith('test@');
}

function FormModal({ exhibitionId, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', organization_name: '', role_title: '', category: 'curator',
    city: '', country: '', website: '', instagram: '', short_description: '', why_relevant: '',
    priority: 'normal',
  });
  const [busy, setBusy] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (busy) return;
    if (!form.name && !form.email) { toast.error('Provide at least a name or email.'); return; }
    setBusy(true);
    try {
      await inviteApi.createContact({ ...form, exhibition_id: exhibitionId, audience_type: form.category, source_type: 'manual', email_confidence: 'unknown' });
      toast.success('Contact created');
      onCreated?.();
    } catch (err) {
      toast.error(errorDetail(err, 'Create failed'));
    } finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-[#222] w-full max-w-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#90E0EF]">Add Invite Contact</p>
          <button onClick={onClose} data-clickable="true" className="text-[#777] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name" value={form.name} onChange={update('name')} testid="form-contact-name" />
          <Input label="Email" value={form.email} onChange={update('email')} testid="form-contact-email" />
          <Input label="Organization" value={form.organization_name} onChange={update('organization_name')} />
          <Input label="Role" value={form.role_title} onChange={update('role_title')} />
          <SelectInput label="Category" value={form.category} onChange={update('category')} options={AUDIENCE_TYPES} />
          <SelectInput label="Priority" value={form.priority} onChange={update('priority')} options={PRIORITIES} />
          <Input label="City" value={form.city} onChange={update('city')} />
          <Input label="Country" value={form.country} onChange={update('country')} />
          <Input label="Website" value={form.website} onChange={update('website')} />
          <Input label="Instagram" value={form.instagram} onChange={update('instagram')} />
        </div>
        <Input label="Short description" value={form.short_description} onChange={update('short_description')} />
        <Input label="Why relevant" value={form.why_relevant} onChange={update('why_relevant')} />
        <div className="flex justify-end gap-2 pt-2 border-t border-[#1a1a1a]">
          <button onClick={onClose} data-clickable="true" className="text-[10px] font-mono px-3 py-2 border border-[#333] text-[#aaa] hover:text-white uppercase tracking-[0.14em]">Cancel</button>
          <button onClick={submit} disabled={busy} data-clickable="true" data-testid="form-contact-submit" className="text-[10px] font-mono px-3 py-2 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.14em]">{busy ? 'Saving…' : 'Create'}</button>
        </div>
      </div>
    </motion.div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      <input value={value} onChange={onChange} data-testid={testid} className="invite-input w-full" />
    </label>
  );
}
function SelectInput({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      <select value={value} onChange={onChange} className="invite-select w-full">
        {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    </label>
  );
}

function errorDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.detail || d.message || fallback;
  return err?.message || fallback;
}
